import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { mapGuidelineToDtoForSave } from 'libs/shared/domain/src/lib/infrastructure/guideline-page-domain/util/guideline.service.util';
import { map, Observable, of, throwError } from 'rxjs';
import { catchError, shareReplay, switchMap } from 'rxjs/operators';
import { MessagesService, ConcurrencyChangesDialogComponent, ConcurrencyChangesDialogType, mapDtoToGuideline } from 'shared/domain';
import { DialogService } from 'shared/ui-common';
import { isTruthy, LANG, ServerResponse, TreetopPropertiesProvider } from 'shared/util-common';

import {
  GuidelineHubInformationDto,
  Pagination,
  ValidateImportResultDto,
  UserDto,
  GuidelineState,
  GuidelineEditTrackingDto,
  GuidelineDto,
  ConcurrencyChangeType,
  ConcurrencyChangesDto,
  ActivityLogDto,
  GuidelineImport,
  Ucum,
  GuidelineSourceSettings,
  UpdateStatus,
  Guideline,
  GuidelineReview,
  GuidelineReviewStatus,
  GuidelineFilter,
  SelectableGuideline,
  GuidelineCodingPageQueryParams,
  GuidelineBasicInfo,
} from '@treetop/shared/model';

import { createActivityStreamParams, createGuidelineDetailParams, createGuidelineParams } from './guidelines.http.service.util';
import { GuidelineSummary } from '../entities/guideline-summary';
import { Sorting } from '../entities/sorting';

const defaultGuidelineFilter = {
  searchText: '',
  guidelineStates: [],
  organizationIds: [],
  owners: [],
  authors: [],
  showSharedGuidelines: false,
  showImportedGuidelines: true,
  showSoonToExpire: false,
  showExpired: false,
};

interface PaginatedResponse<T> extends Pagination {
  list: T[];
}

export type GuidelinePatchParams = {
  ownerId?: string;
  authors?: UserDto[];
  updateStatus?: UpdateStatus;
};

type GetGuidelinesResponse = PaginatedResponse<GuidelineSummary>;

export type GuidelineWithPublishedResponseDto = {
  latest: GuidelineDto;
  published: GuidelineDto;
};

export type GuidelineReviewActivityDto = {
  id: string;
  status: GuidelineReviewStatus;
  updatedAt: string;
};

export type GuidelineReviewDto = {
  id: string;
  guidelineUniqueId: string;
  guidelineVersion: number;
  reviewerId?: string;
  reviewer: UserDto;
  status: GuidelineReviewStatus;
  updatedAt: string;
  isActive: boolean;
  assignedById?: string;
  assignedBy: UserDto;
  assignedAt: string;
  unAssignedById?: string;
  unAssignedBy?: UserDto;
  unAssignedAt?: string;
  guidelineReviewActivities: GuidelineReviewActivityDto[];
};

export type GuidelineWithPublishedResponse = {
  latest: Guideline;
  published: Guideline;
};

export type GuidelineGetOptions = {
  filter?: Partial<GuidelineFilter>;
  sorting?: Sorting;
  usableOnly?: boolean;
};

export type GuidelineApproveOptions = {
  approvalNote?: string;
};

export type GuidelinePublishOptions = {
  tagEditedGuideline?: Guideline;
  revisionDate?: Date | Ucum;
  updateMessage?: string;
  sendEmailNotification?: boolean;
  disableAllTags?: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class GuidelinesHttpService {
  private http = inject(HttpClient);
  private dialog = inject(DialogService);
  private messages = inject(MessagesService);

  private baseUrl = `${TreetopPropertiesProvider.properties.baseUrl}api`;

  public get(page: number, pageSize: number, options?: GuidelineGetOptions): Observable<ServerResponse<GuidelineSummary>> {
    const _options = {
      ...options,
      filter: {
        ...defaultGuidelineFilter,
        ...options?.filter,
      },
    };

    const params = createGuidelineParams({ page, itemsPerPage: pageSize }, _options);

    return this.http.get<GetGuidelinesResponse>(`${this.baseUrl}/guidelines`, { params }).pipe(
      map((response) => ({
        list: response.list,
        page: response.page,
        itemsPerPage: response.itemsPerPage,
        maxPages: response.maxPage,
        maxElements: response.maxElements,
      })),
    );
  }

  public import(importData: GuidelineImport): Observable<Guideline> {
    return this.http.post<GuidelineDto>(`${this.baseUrl}/guidelines/import`, importData).pipe(map(mapDtoToGuideline));
  }

  public add(guideline: Guideline): Observable<Guideline> {
    return this.http.post<GuidelineDto>(`${this.baseUrl}/guidelines`, mapGuidelineToDtoForSave(guideline)).pipe(map(mapDtoToGuideline));
  }

  public update(guideline: Guideline): Observable<Guideline> {
    return this.isEditAllowed(guideline, true).pipe(
      switchMap((isAllowed) => {
        if (isAllowed) {
          return this.http
            .put<GuidelineDto>(`${this.baseUrl}/guidelines/${guideline.uniqueId}`, mapGuidelineToDtoForSave(guideline))
            .pipe(map(mapDtoToGuideline));
        } else {
          return throwError(() => new Error('Concurrency changes detected'));
        }
      }),
    );
  }

  public validateImport(guidelineImport: Partial<GuidelineImport>): Observable<ValidateImportResultDto> {
    return this.http.post<ValidateImportResultDto>(`${this.baseUrl}/guidelines/validate-import`, guidelineImport);
  }

  public patch(uniqueId: string, params: Partial<GuidelinePatchParams>): Observable<SelectableGuideline> {
    return this.http.patch<SelectableGuideline>(`${this.baseUrl}/guidelines/${uniqueId}`, params);
  }

  public editImportGuidelineSettings(uniqueId: string, params: Partial<GuidelineSourceSettings>): Observable<SelectableGuideline> {
    return this.http.post<SelectableGuideline>(`${this.baseUrl}/guidelines/${uniqueId}/update-settings`, params);
  }

  public detachFromUpdateCycle(uniqueId: string): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/guidelines/${uniqueId}/detach`, {});
  }

  public edit(uniqueId: string, guideline: Guideline): Observable<Guideline> {
    return this.http
      .put<GuidelineDto>(`${this.baseUrl}/guidelines/${uniqueId}`, mapGuidelineToDtoForSave(guideline))
      .pipe(map(mapDtoToGuideline));
  }

  public acceptUpdate(uniqueId: string, guideline: Guideline): Observable<Guideline> {
    return this.http
      .put<GuidelineDto>(`${this.baseUrl}/guidelines/${uniqueId}/accept-update`, mapGuidelineToDtoForSave(guideline))
      .pipe(map(mapDtoToGuideline));
  }

  public assignGuidelineToTaxonomy(guidelineId: string, taxonomyId: string): Observable<void> {
    const params = new HttpParams().set('taxonomyId', taxonomyId);

    return this.http.put<void>(`${this.baseUrl}/guidelines/${guidelineId}/assign-taxonomy`, null, { params });
  }

  public getRecentlyEdited(count = 4): Observable<GuidelineSummary[]> {
    const params = new HttpParams().set('count', count);

    return this.http.get<GuidelineSummary[]>(`${this.baseUrl}/guidelines/recently-edited`, { params });
  }

  public getByIdWithPublished(uniqueId: string): Observable<GuidelineWithPublishedResponse> {
    return this.http.get<GuidelineWithPublishedResponseDto>(`${this.baseUrl}/guidelines/with-published/${uniqueId}`).pipe(
      map((response) => {
        return {
          latest: mapDtoToGuideline(response.latest),
          published: mapDtoToGuideline(response.published),
        };
      }),
    );
  }

  public getById(uniqueId: string): Observable<Guideline> {
    const params = createGuidelineDetailParams();
    return this.http.get<GuidelineDto>(`${this.baseUrl}/guidelines/${uniqueId}`, { params }).pipe(map((dto) => mapDtoToGuideline(dto)));
  }

  public getActivityStream(id: string, getForAllVersions = true, fromDate?: Date, toDate?: Date): Observable<ActivityLogDto[]> {
    const params = createActivityStreamParams(getForAllVersions, fromDate, toDate);

    return this.http.get<ActivityLogDto[]>(`${this.baseUrl}/guidelines/${id}/activity-stream`, { params });
  }

  public getGuidelineVersions(uniqueId: string): Observable<SelectableGuideline[]> {
    const language = localStorage.getItem(LANG);
    if (language) {
      return this.http.get<SelectableGuideline[]>(`${this.baseUrl}/guidelines/${uniqueId}/versions?languageCode=${language}`);
    }
    return this.http.get<SelectableGuideline[]>(`${this.baseUrl}/guidelines/${uniqueId}/versions`);
  }

  public deleteGuideline(uniqueId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/guidelines/${uniqueId}`);
  }

  public approveGuideline(guideline: Guideline, options?: GuidelineApproveOptions): Observable<GuidelineDto> {
    return this.isEditAllowed(guideline).pipe(
      isTruthy(),
      switchMap(() => {
        return this.http.put<GuidelineDto>(`${this.baseUrl}/guidelines/${guideline.uniqueId}/approve`, options);
      }),
    );
  }

  public publishGuideline(guideline: Guideline, options?: GuidelinePublishOptions): Observable<Guideline> {
    return this.isEditAllowed(guideline).pipe(
      switchMap((isAllowed) => {
        if (!isAllowed) {
          return of({});
        }

        return this.http
          .put<GuidelineDto>(`${this.baseUrl}/guidelines/${guideline.uniqueId}/publish`, {
            ...options,
            tagEditedGuideline: mapGuidelineToDtoForSave(guideline),
          })
          .pipe(map(mapDtoToGuideline));
      }),
    );
  }

  public postGuidelineState(guideline: Guideline, state: GuidelineState): Observable<Guideline> {
    const statesRecord: Record<number, string> = {
      0: 'in-progress',
      1: 'in-review',
      2: 'approve',
      3: 'publish',
      4: 'inactive',
    };

    return this.isEditAllowed(guideline).pipe(
      switchMap((isAllowed) => {
        if (isAllowed) {
          return this.http
            .put<GuidelineDto>(`${this.baseUrl}/guidelines/${guideline.uniqueId}/${statesRecord[state]}`, null)
            .pipe(map(mapDtoToGuideline));
        } else {
          return of({});
        }
      }),
    );
  }

  public unassignGuidelineTaxonomy(uniqueId: string, taxonomyId: string) {
    return this.http.put<void>(`${this.baseUrl}/guidelines/${uniqueId}/unassign-taxonomy?taxonomyId=${taxonomyId}`, {});
  }

  public matching(params: GuidelineCodingPageQueryParams): Observable<GuidelineBasicInfo[]> {
    return this.http.get<GuidelineBasicInfo[]>(`${this.baseUrl}/guidelines/matching`, {
      params: new HttpParams().set('systemId', params.systemId).set('versionId', params.version).set('code', params.code),
    });
  }

  public changeHubSyncStatus(guidelineHubInformation: GuidelineHubInformationDto): Observable<GuidelineHubInformationDto> {
    return this.http.post<GuidelineHubInformationDto>(`${this.baseUrl}/guidelines/change-hub-sync-status`, guidelineHubInformation);
  }

  public assignReviewers(guidelineUniqueId: string, reviewerIds: string[]): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/guidelines/${guidelineUniqueId}/assign-reviewers`, reviewerIds);
  }

  public addReviewers(guidelineUniqueId: string, reviewerIds: string[]): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/guidelines/${guidelineUniqueId}/add-reviewers`, reviewerIds);
  }

  public removeReviewers(guidelineUniqueId: string, reviewerIds: string[]): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/guidelines/${guidelineUniqueId}/remove-reviewers`, reviewerIds);
  }

  public requestReview(reviewId: string): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/guidelines/${reviewId}/request-review`, {});
  }

  public reviewGuideline(guidelineUniqueId: string, status: GuidelineReviewStatus): Observable<GuidelineReview> {
    const params = new HttpParams().set('status', status);
    return this.http.put<GuidelineReview>(`${this.baseUrl}/guidelines/${guidelineUniqueId}/review`, null, { params });
  }

  public checkConcurrencyChanges(updatedGuideline: GuidelineDto): Observable<ConcurrencyChangesDto> {
    return this.http.post<ConcurrencyChangesDto>(
      `${this.baseUrl}/guidelines/${updatedGuideline.id}/concurrency-changes`,
      mapGuidelineToDtoForSave(updatedGuideline as Guideline),
    );
  }

  private isEditAllowed(guideline: GuidelineDto, enableForceOverwrite = false): Observable<boolean> {
    return this.http
      .post<ConcurrencyChangesDto>(
        `${this.baseUrl}/guidelines/${guideline.id}/concurrency-changes`,
        mapGuidelineToDtoForSave(guideline as Guideline),
      )
      .pipe(
        switchMap((concurrencyChanges) => {
          if (concurrencyChanges.hasChanged) {
            if (concurrencyChanges.type === ConcurrencyChangeType.Content && enableForceOverwrite) {
              const dialogRef = this.dialog.open(ConcurrencyChangesDialogComponent, {
                title: 'EditingConflict',
                data: {
                  type: ConcurrencyChangesDialogType.Guideline,
                  concurrencyChanges: concurrencyChanges,
                },
              });
              return dialogRef.afterClosed().pipe(
                map((forceOverwrite?: boolean) => {
                  return !!forceOverwrite;
                }),
              );
            } else {
              concurrencyChanges.type =
                concurrencyChanges.type !== ConcurrencyChangeType.Content ? concurrencyChanges.type : ConcurrencyChangeType.None;
              this.dialog.open(ConcurrencyChangesDialogComponent, {
                title: 'EditingConflict',
                data: {
                  type: ConcurrencyChangesDialogType.Guideline,
                  concurrencyChanges: concurrencyChanges,
                },
              });
              return of(false);
            }
          } else {
            return of(true);
          }
        }),
        catchError((err: HttpErrorResponse) => {
          // Workaround to show message to the user until we change the logic in flowchart to
          // force the user to select a component
          if (
            err?.error?.errors?.guidelineActionId &&
            err?.error?.errors?.guidelineActionId.length > 0 &&
            err.error.errors.guidelineActionId[0].includes('{null}')
          ) {
            this.messages.showError('FlowchartMakeSureAllNodesAreSet');
          } else {
            this.messages.showError('ConcurrencyChangeCheckError');
          }
          return throwError(() => err);
        }),
        shareReplay(),
      );
  }

  public trackEdit(uniqueId: string) {
    this.http
      .post(`${this.baseUrl}/guidelines/${uniqueId}/track-edit`, null)
      .pipe(
        catchError((err: HttpErrorResponse) => {
          this.messages.showError('SaveError');
          return throwError(() => err);
        }),
      )
      .subscribe();
  }

  public endEdit(uniqueId: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/guidelines/${uniqueId}/end-edit`, null).pipe(
      catchError((err: HttpErrorResponse) => {
        this.messages.showError('SaveError');
        console.error('Error:', err);
        return throwError(() => err);
      }),
    );
  }

  public currentlyEditing(uniqueId: string): Observable<GuidelineEditTrackingDto[]> {
    return this.http.get<GuidelineEditTrackingDto[]>(`${this.baseUrl}/guidelines/${uniqueId}/currently-editing`);
  }

  public getPublishedUniqueIds(): Observable<string[]> {
    return this.http.get<string[]>(`${this.baseUrl}/smart-book/published-unique-ids`);
  }
}
